import { Box, Typography } from '@mui/material';
import {
  DynamicBadge,
  type IStylesOverrides,
} from '@orcha/micro-library-core-ui';
import type { ImageProps } from 'next/image';

export type ThresholdImageWidgetStyleOverrides = IStylesOverrides & {
  badgeImageWidth?: number;
  badgeImageHeight?: number;
  badgePadding?: number;
  boxMargin?: number;
};

export type ThresholdImageWidgetConfiguration = {
  imageUrl: string;
  minValue: number;
  maxValue: number;
};

export type ThresholdImageWidgetProps = {
  title: string;
  value: number;
  thresholds: ThresholdImageWidgetConfiguration[];
  FlexibleImageComponent: React.ComponentType<ImageProps>;
  styleOverrides?: ThresholdImageWidgetStyleOverrides;
  translatedText: {
    tooltipText: string;
  };
  testId?: string;
};

/**
 * ThresholdImageWidget from @orcha/micro-library-app-details-ui
 * @param {React.ComponentType<ImageProps>} FlexibleImageComponent - The component to use for the image
 * @param {IStylesOverrides & { width?: number; height?: number }} styleOverrides - The styles to apply to the widget
 * @param {string} title - The title to display in the widget
 * @param {number} value - The app score value to check against the thresholds
 * @param {Threshold[]} thresholds - The thresholds to display in the widget
 * @param {string} translatedText.tooltipText - The tooltip text used in the tooltip of the DynamicBadge
 * @param {string} testId - The test id to use for the widget (optional)
 */

export const ThresholdImageWidget = ({
  title,
  value,
  thresholds,
  styleOverrides,
  FlexibleImageComponent,
  translatedText,
  testId = 'threshold-image-summary-widget-text',
}: ThresholdImageWidgetProps) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      data-testid={testId}
      sx={{ margin: styleOverrides?.boxMargin ?? 0 }}
    >
      <Typography
        sx={{ fontWeight: 'bold', fontSize: '14px', textOverflow: 'ellipsis' }}
        variant="body1"
      >
        {title ?? ''}
      </Typography>

      <Box
        sx={{
          padding: styleOverrides?.badgePadding ?? '0.5rem',
        }}
      >
        <DynamicBadge
          value={value}
          thresholds={thresholds}
          FlexibleImageComponent={FlexibleImageComponent}
          translatedText={translatedText}
          width={styleOverrides?.badgeImageWidth ?? 64}
          height={styleOverrides?.badgeImageHeight ?? 64}
        />
      </Box>
    </Box>
  );
};
